import { useCallback, useMemo, useState } from "react";
import { Permission } from "./types/permissions";
import {
  CRUD_VERBS,
  ModelGroup,
  buildGroupedByApp,
  isSystemApp,
  modelPermIds,
  modelsPermIds,
} from "./permissionUtils";

export type AppFilter = "all" | "selected" | "unselected";
export type BulkAction = "selectAll" | "clearAll" | "readOnly" | "fullCrud";

/**
 * Shared state/logic behind the app/model/permission picker used by both
 * the role permission editor and the add-role wizard's permissions step.
 * Keeps `granted` controlled by the caller so each screen owns its own
 * source of truth (existing role vs. a role being created).
 */
export function usePermissionsPicker(
  allPermissions: Permission[],
  granted: Set<number>,
  setGranted: React.Dispatch<React.SetStateAction<Set<number>>>,
) {
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedApps, setExpandedApps] = useState<Record<string, boolean>>({});
  const [showSystemApps, setShowSystemApps] = useState(false);
  const [showEventModels, setShowEventModels] = useState(false);
  const [appFilter, setAppFilter] = useState<AppFilter>("all");

  const togglePermission = useCallback(
    (id: number) => {
      setGranted((prev) => {
        const next = new Set(prev);
        if (next.has(id)) {
          next.delete(id);
        } else {
          next.add(id);
        }
        return next;
      });
    },
    [setGranted],
  );

  const toggleApp = useCallback((app: string) => {
    setExpandedApps((prev) => ({ ...prev, [app]: !prev[app] }));
  }, []);

  const setIdsGranted = useCallback(
    (ids: number[], value: boolean) => {
      setGranted((prev) => {
        const next = new Set(prev);
        ids.forEach((id) => (value ? next.add(id) : next.delete(id)));
        return next;
      });
    },
    [setGranted],
  );

  const toggleAllPermissionsForApp = useCallback(
    (models: ModelGroup[]) => {
      const ids = modelsPermIds(models);
      const allSelected = ids.every((id) => granted.has(id));
      setIdsGranted(ids, !allSelected);
    },
    [granted, setIdsGranted],
  );

  const toggleFullCrudForModel = useCallback(
    (m: ModelGroup) => {
      const crudIds = CRUD_VERBS.map((v) => m.crud[v]?.id).filter(
        (id): id is number => id !== undefined,
      );
      const allSelected = crudIds.every((id) => granted.has(id));
      setIdsGranted(crudIds, !allSelected);
    },
    [granted, setIdsGranted],
  );

  const groupedByApp = useMemo(
    () => buildGroupedByApp(allPermissions),
    [allPermissions],
  );

  const appHasSelection = useCallback(
    (models: ModelGroup[]) =>
      modelsPermIds(models).some((id) => granted.has(id)),
    [granted],
  );

  const visibleGrouped = useMemo(() => {
    const term = searchTerm.toLowerCase().trim();
    const result: Record<string, ModelGroup[]> = {};

    Object.entries(groupedByApp).forEach(([app, models]) => {
      if (!showSystemApps && isSystemApp(app)) return;

      let visibleModels = showEventModels
        ? models
        : models.filter((m) => !m.isEventTwin);

      if (term) {
        visibleModels = visibleModels.filter(
          (m) =>
            m.model.toLowerCase().includes(term) ||
            app.toLowerCase().includes(term) ||
            m.all.some((p) => p.name.toLowerCase().includes(term)),
        );
      }

      if (visibleModels.length === 0) return;

      const selected = appHasSelection(visibleModels);
      if (appFilter === "selected" && !selected) return;
      if (appFilter === "unselected" && selected) return;

      result[app] = visibleModels;
    });

    return result;
  }, [
    groupedByApp,
    searchTerm,
    showSystemApps,
    showEventModels,
    appFilter,
    appHasSelection,
  ]);

  const hiddenCounts = useMemo(() => {
    let systemApps = 0;
    let eventModels = 0;
    Object.entries(groupedByApp).forEach(([app, models]) => {
      if (isSystemApp(app)) {
        systemApps += models.reduce((n, m) => n + m.all.length, 0);
      } else {
        eventModels += models
          .filter((m) => m.isEventTwin)
          .reduce((n, m) => n + m.all.length, 0);
      }
    });
    return { systemApps, eventModels };
  }, [groupedByApp]);

  const isAppFullyGranted = useCallback(
    (models: ModelGroup[]) =>
      modelsPermIds(models).every((id) => granted.has(id)),
    [granted],
  );

  const isAppPartiallyGranted = useCallback(
    (models: ModelGroup[]) => {
      const ids = modelsPermIds(models);
      const some = ids.some((id) => granted.has(id));
      const all = ids.every((id) => granted.has(id));
      return some && !all;
    },
    [granted],
  );

  const handleBulk = useCallback(
    (action: BulkAction) => {
      Object.values(visibleGrouped).forEach((models) => {
        models.forEach((m) => {
          const crudIds = CRUD_VERBS.map((v) => m.crud[v]?.id).filter(
            (id): id is number => id !== undefined,
          );
          const otherIds = m.other.map((p) => p.id);

          if (action === "selectAll") {
            setIdsGranted([...crudIds, ...otherIds], true);
          } else if (action === "clearAll") {
            setIdsGranted([...crudIds, ...otherIds], false);
          } else if (action === "readOnly") {
            setIdsGranted(otherIds, false);
            const nonView = CRUD_VERBS.filter((v) => v !== "view")
              .map((v) => m.crud[v]?.id)
              .filter((id): id is number => id !== undefined);
            setIdsGranted(nonView, false);
            if (m.crud.view) setIdsGranted([m.crud.view.id], true);
          } else if (action === "fullCrud") {
            setIdsGranted(otherIds, false);
            setIdsGranted(crudIds, true);
          }
        });
      });
    },
    [visibleGrouped, setIdsGranted],
  );

  const stats = useMemo(() => {
    let apps = 0;
    let models = 0;
    let permissions = 0;
    let custom = 0;
    Object.values(groupedByApp).forEach((modelGroups) => {
      let appSelected = false;
      modelGroups.forEach((m) => {
        const ids = modelPermIds(m);
        const modelGranted = ids.filter((id) => granted.has(id));
        if (modelGranted.length > 0) {
          models++;
          appSelected = true;
        }
        CRUD_VERBS.forEach((v) => {
          if (m.crud[v] && granted.has(m.crud[v]!.id)) permissions++;
        });
        m.other.forEach((p) => {
          if (granted.has(p.id)) custom++;
        });
      });
      if (appSelected) apps++;
    });
    return {
      appsSelected: apps,
      modelsSelected: models,
      permissionsSelected: permissions,
      customSelected: custom,
    };
  }, [groupedByApp, granted]);

  return {
    searchTerm,
    setSearchTerm,
    expandedApps,
    toggleApp,
    showSystemApps,
    setShowSystemApps,
    showEventModels,
    setShowEventModels,
    appFilter,
    setAppFilter,
    groupedByApp,
    visibleGrouped,
    hiddenCounts,
    togglePermission,
    setIdsGranted,
    toggleAllPermissionsForApp,
    toggleFullCrudForModel,
    isAppFullyGranted,
    isAppPartiallyGranted,
    handleBulk,
    stats,
  };
}
